import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Save } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/project/integrations/supabase/client";
import { Shell, PageHeader } from "@/project/components/Shell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/project/lib/auth";
import {
  SadharmikForm,
  emptySadharmik,
  saveSadharmik,
  validateSadharmik,
  loadSadharmik,
  type SadharmikValues,
} from "@/project/components/SadharmikForm";
import {
  ApplicationForm,
  emptyApplication,
  toRow,
  validateApplication,
  type AppFormValues,
} from "@/project/components/ApplicationForm";
import { MemberPicker } from "@/project/components/MemberPicker";
import { LetterStep } from "@/project/components/LetterStep";

export const Route = createFileRoute("/applications/new")({
  validateSearch: (search: Record<string, unknown>): { family?: string } =>
    typeof search["family"] === "string"
      ? { family: search["family"] as string }
      : {},
  head: () => ({
    meta: [
      { title: "New Sadharmik Registration · Assistance & Letter Desk" },
      {
        name: "description",
        content:
          "Register a new sadharmik household with family member details and record their financial assistance request.",
      },
      { property: "og:title", content: "New Sadharmik Registration" },
      {
        property: "og:description",
        content:
          "Capture household and family member details, then the assistance request.",
      },
    ],
  }),
  component: NewApplicationPage,
});

function NewApplicationPage() {
  const router = useRouter();
  const { user } = useAuth();
  const { family: familyParam } = Route.useSearch();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [family, setFamily] = useState<SadharmikValues>(emptySadharmik());
  const [familyId, setFamilyId] = useState<string | null>(null);
  const [form, setForm] = useState<AppFormValues>(emptyApplication());
  const [appId, setAppId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!familyParam) {
      return;
    }
    loadSadharmik(familyParam)
      .then(v => {
        setFamily(v);
        setFamilyId(familyParam);
        toast.success("Member household loaded");
      })
      .catch(e => toast.error((e as Error).message));
  }, [familyParam]);

  const continueToApplication = async () => {
    const err = validateSadharmik(family);
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      const { familyId: id, rationCard } = await saveSadharmik(
        family,
        user?.id
      );
      setFamilyId(id);
      const head = family.members[0];
      setForm({
        ...emptyApplication(rationCard),
        applicant_name: head?.full_name ?? "",
        applicant_address: family.address,
        applicant_mobile: family.primary_mobile,
        applicant_alt_mobile: family.alt_mobile,
        applicant_aadhaar: head?.aadhaar ?? "",
        bank_account_holder: head?.full_name ?? "",
      });
      setStep(2);
      toast.success("Sadharmik details saved");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const save = async (status: string, goToStep3: boolean) => {
    const payload = { ...form, status };
    const err = validateApplication(payload);
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      if (appId) {
        const { error } = await supabase
          .from("applications")
          .update(toRow(payload, user?.id))
          .eq("id", appId);
        if (error) {
          throw error;
        }
        toast.success("Application updated");
        if (goToStep3) {
          setStep(3);
        } else {
          router.navigate({ to: "/applications/$id", params: { id: appId } });
        }
        return;
      }
      const { data: seqRow } = await supabase
        .from("applications")
        .select("ref_seq")
        .order("ref_seq", { ascending: false })
        .limit(1)
        .maybeSingle();
      const nextSeq = (seqRow?.ref_seq ?? 180) + 1;
      const { data, error } = await supabase
        .from("applications")
        .insert({
          ...toRow(payload, user?.id),
          ref_no: String(nextSeq),
          ref_seq: nextSeq,
          family_id: familyId,
          created_by: user?.id ?? null,
        })
        .select("id")
        .single();
      if (error) {
        throw error;
      }
      setAppId(data.id);
      toast.success("Application saved as " + status);
      if (goToStep3) {
        setStep(3);
      } else {
        router.navigate({ to: "/applications/$id", params: { id: data.id } });
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <PageHeader
        title="New Sadharmik"
        subtitle={
          step === 1
            ? "Step 1 — household and family member details"
            : step === 2
              ? "Step 2 — assistance request details"
              : step === 3
                ? "Step 3 — select organizations & print letters"
                : "Step 4 — letter history"
        }
      />

      <div className="no-print mb-4 flex flex-wrap gap-2 text-sm">
        {[
          { label: "1. Household & family members", ready: true },
          { label: "2. Assistance request", ready: Boolean(familyId) },
          {
            label: "3. Select organizations & print letters",
            ready: Boolean(appId),
          },
          { label: "4. Letter history", ready: Boolean(appId) },
        ].map(({ label, ready }, i) => {
          const target = (i + 1) as 1 | 2 | 3 | 4;
          const active = target === step;
          return (
            <button
              key={label}
              type="button"
              disabled={!ready}
              onClick={() => {
                if (!ready) {
                  toast.error(
                    "Please complete and save the current step first."
                  );
                  return;
                }
                setStep(target);
              }}
              className={`rounded-full border px-3 py-1 transition-colors ${
                active
                  ? "border-primary bg-primary text-primary-foreground"
                  : ready
                    ? "hover:border-primary/60 hover:bg-primary/5 border-border bg-background text-foreground"
                    : "cursor-not-allowed border-border text-muted-foreground opacity-60"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {step === 1 && (
        <>
          <SadharmikForm value={family} onChange={setFamily} />
          <div className="no-print bg-background/95 sticky bottom-0 mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border py-4 backdrop-blur">
            <Button
              size="lg"
              variant="outline"
              onClick={() => router.navigate({ to: "/members" })}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button size="lg" disabled={busy} onClick={continueToApplication}>
              Save &amp; Next: Assistance Request{" "}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="mb-4">
            <MemberPicker
              members={family.members}
              value={form}
              onChange={setForm}
            />
          </div>
          <ApplicationForm value={form} onChange={setForm} />
          <div className="no-print bg-background/95 sticky bottom-0 mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border py-4 backdrop-blur">
            <Button
              size="lg"
              variant="outline"
              disabled={busy}
              onClick={() => setStep(1)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                variant="ghost"
                disabled={busy}
                onClick={() => save("Profile Incomplete", false)}
              >
                Quick Save (Profile Incomplete)
              </Button>
              <Button
                size="lg"
                disabled={busy}
                onClick={() => save("Ready for Letter", true)}
              >
                Save &amp; Next: Organization Letters{" "}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      {step === 3 && appId && (
        <>
          <LetterStep applicationId={appId} section="letters" />
          <div className="no-print bg-background/95 sticky bottom-0 mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border py-4 backdrop-blur">
            <Button size="lg" variant="outline" onClick={() => setStep(2)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button size="lg" onClick={() => setStep(4)}>
              Next: Letter History <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </>
      )}

      {step === 4 && appId && (
        <>
          <LetterStep applicationId={appId} section="history" />
          <div className="no-print bg-background/95 sticky bottom-0 mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border py-4 backdrop-blur">
            <Button size="lg" variant="outline" onClick={() => setStep(3)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button
              size="lg"
              onClick={() =>
                router.navigate({
                  to: "/applications/$id",
                  params: { id: appId },
                })
              }
            >
              <Save className="mr-2 h-4 w-4" /> Finish &amp; Save
            </Button>
          </div>
        </>
      )}
    </Shell>
  );
}
